<template>
  <div v-if="(event && checkCOVID()) || expense || stipend || request" class="document-upload">
    <div v-if="!stipend && !request" class="form-field document-upload-header">
      <h4 v-if="name" class="form-field-header">{{ name }}</h4>
      <div
        v-if="expense"
        v-html="subheading"
        class="subheading"
      />
      <div class="form-field-container">
        <div v-if="description" v-html="description" class="document-upload-description"></div>
      </div>
    </div>
    <div class="form-field document-upload-container">
      <!-- File upload -->
      <h5 class="document-upload-heading" v-translate>Document upload</h5>
      <div class="form-field-container click-to-upload">
        <span v-translate>Click upload to add a document</span>
      </div>
      <div class="form-field-container document-upload-button">
        <keep-alive>
          <DocumentFilePicker
            v-if="!loading"
            key="emptyFilePicker"
            @loaded="onFileLoaded($event)"
            ref="chooser"
            :readAsString="false"
            :documents.sync="documentList"
            :darkText="darkText"
            :actionName="actionName"
          />
          <div class="loading" v-else>
            <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
          </div>
        </keep-alive>
      </div>

      <!-- Uploaded document(s) list -->
      <div class="form-field-container document-uploads">
        <div class="document-uploads-header"><span v-translate>Uploaded Documents</span></div>
        <DocumentFilePicker
          v-for="doc in documentList"
          :key="doc.key"
          :filename="doc.filename"
          :file="doc.file"
          @reset="onReset(doc)"
          ref="chooser"
          :readAsString="false"
          :disableDelete="!currentRequestSubmitted(currentRequest) || disableDelete"
          :darkText="darkText"
        />
        <div v-if="documentsListEmpty">{{ $gettext('No document uploaded') }}</div>
      </div>
    </div>

    <!-- Additional question -->
    <div
      v-if="
        event &&
          commonCategories() &&
          documentList.length &&
          organizationDetails.expenseDocumentationEnabled &&
          organizationDetails.eventDocumentationEnabled
      "
      class="form-field additional-question-card"
    >
      <h4 class="form-field-header">
        *
        <translate
          >Does at least one of your documents show the out-of-pocket cost of the
          emergency?</translate
        >
      </h4>
      <div class="form-field-container">
        <div class="radio-buttons additional-question-radios">
          <label class="radio-button">
            <input
              type="radio"
              v-model="docTypeDetail"
              @change="onDocTypeSelection"
              value="invoice"
            />
            <span>{{ ADDITIONAL_QUESTION_TEXT_CHOICES['invoice'] }}</span>
          </label>
          <label class="radio-button">
            <input
              type="radio"
              v-model="docTypeDetail"
              @change="onDocTypeSelection"
              value="receipt"
            />
            <span>{{ ADDITIONAL_QUESTION_TEXT_CHOICES['receipt'] }}</span>
          </label>
          <label class="radio-button">
            <input
              type="radio"
              v-model="docTypeDetail"
              @change="onDocTypeSelection"
              value="estimate"
            />
            <span>{{ ADDITIONAL_QUESTION_TEXT_CHOICES['estimate'] }}</span>
          </label>
          <label class="radio-button">
            <input type="radio" v-model="docTypeDetail" @change="onDocTypeSelection" value="none" />
            <span>{{ ADDITIONAL_QUESTION_TEXT_CHOICES['none'] }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import DocumentFilePicker from '@/components/DocumentFilePicker'
import LoadSpinner from '@/components/LoadSpinner'

import Document from '@/services/documents'
import EventCategory from '@/services/eventCategories'
import Utils from '@/services/utils'
import { formatValue } from '@/services/utils'
import Request from '@/services/requests'
import { customVariablesFromBackendTranslationFilter } from '../../services/organizationCustomVariables'

export default {
  name: 'DocumentUpload',
  components: {
    DocumentFilePicker,
    LoadSpinner,
  },
  props: {
    documentList: {
      type: Array,
      default: () => [],
      required: true,
    },
    event: {
      type: Object,
      default: null,
      required: false,
    },
    expense: {
      type: Object,
      default: null,
      required: false,
    },
    stipend: {
      type: Object,
      default: null,
      require: false,
    },
    request: {
      type: Object,
      default: null,
      require: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    docDetailType: {
      type: String,
      default: null,
    },
    darkText: {
      type: Boolean,
      default: false,
    },
    actionName: {
      type: String,
      default: null,
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filePickerCount: 1,
      files: {},
      uploaded: false,
      docTypeDetail: this.docDetailType,
      ADDITIONAL_QUESTION_TEXT_CHOICES: {
        invoice: this.$gettext('Yes, I have uploaded an invoice showing amount owed and due date'),

        receipt: this.$gettext('Yes, I have uploaded a receipt showing amount paid and date'),

        estimate: this.$gettext(
          'Yes, I have uploaded an estimate because I don’t have a receipt or invoice',
        ),

        none: this.$gettext('No'),
      },
    }
  },
  methods: {
    /**
     * checks whether COVID19  is a selected event category
     * if true no documentaion uploader will be shown
     * all other event categories will display documemtation uploader
     */
    checkCOVID() {
      if (this.event) {
        return this.event.categoryRef.name !== EventCategory.options.COVID19
      }
    },
    currentRequestSubmitted(request) {
      return !Request.options.CLOSED_REQUEST_STATUSES.includes(request.status)
    },
    onFileLoaded(file) {
      // For each new file, create a document object
      const doc = Document.create({
        file: file,
        filename: file.name,
      })

      // Add event or expense information
      if (this.event) {
        doc.event = this.event.id
      } else if (this.expense) {
        doc.expense = this.expense.id
      } else if (this.stipend) {
        doc.stipend = this.stipend.id
      } else if (this.request) {
        doc.request = this.request.id
      }

      // Add it to our list and emit to the parent
      this.documentList.push(doc)
      this.$emit('documents-changed', this.documentList)
    },
    onReset(doc) {
      const index = this.documentList.findIndex(d => d.key === doc.key)
      this.$emit('delete-document', doc)
      this.$emit('document-detail-type-selected', 'remove')
      this.docTypeDetail = null
    },
    onDocTypeSelection() {
      this.$emit('document-detail-type-selected', this.docTypeDetail)
    },
    commonCategories() {
      return [
        EventCategory.options.HOME,
        EventCategory.options.CAR,
        EventCategory.options.MEDICAL,
      ].includes(this.event?.categoryRef?.name)
    },
  },
  computed: {
    ...mapState(['organizationDetails', 'organizationCssVars', 'currentRequest']),
    name() {
      return this?.event?.categoryRef?.name
    },
    subheading() {
      return this?.expense?.categoryRef?.descriptionHtml
    },
    description() {
      if (this.event) {
        return this.event?.categoryRef && this.event?.categoryRef?.documentationDescLongHtml
      }
      else if (this.expense) {
        const varMap = { org: this.organizationDetails }
        return formatValue(this.expense?.categoryRef?.documentationDescHtml, varMap)
      }

      return null
    },
    amount() {
      if (!this.expense) {
        return null
      }

      const inputAmount = Number.parseInt(this.expense.amount, 10)

      if (Number.isNaN(inputAmount)) {
        return null
      }

      return inputAmount
    },
    documentsListEmpty() {
      return this.documentList.length == 0
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/grant-circles';

.document-upload {
  margin-bottom: 2.5rem;

  .document-upload-header,
  .additional-question-card {
    @extend .card-shape;

    background-color: var(--light-brand-color);
    box-shadow: none;
    -webkit-box-shadow: none;
  }

  .document-upload-header {
    .form-field-header {
      font-size: 1.5rem;
      line-height: 2rem;
    }

    .subheading {
      margin-bottom: 1.5rem;

      ::v-deep p {
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.75rem;
      }
    }

    .document-upload-description::v-deep {
      p {
        margin: 0;
      }

      p:nth-of-type(1) {
        font-size: 1.125rem;
        line-height: 1.5rem;
      }

      p:nth-of-type(2) {
        font-size: 1.125rem;
      }
    }
  }

  .additional-question-radios {
    margin-top: 1.5rem;
  }
}

.document-upload-container {
  @extend .card-shape;

  .document-upload-heading {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: bold;
  }

  .error-text {
    padding: 0;
  }

  .click-to-upload {
    font-size: 0.75rem;
    line-height: 1rem;
    margin-bottom: 1rem;
  }

  .document-upload-button {
    margin-bottom: 1.5rem;
  }

  .document-uploads {
    .document-uploads-header {
      font-size: 0.75rem;
      font-weight: bold;
      line-height: 1rem;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
    }

    .document-file-picker-container {
      margin-bottom: 0.25rem;

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
}
</style>
